<template>
  <VaAccordion v-model="value">
    <VaCollapse v-if="item.route || item.children" :key="item.id">
      <template #header="{ value: isCollapsed }">
        <VaSidebarItem
          :to="item.children ? undefined : { name: item.route }"
          :active="routeHasActiveChild(item)"
          :active-color="activeColor"
          :text-color="textColor(item)"
          :aria-label="`${item.children ? 'Open category ' : 'Visit'} ${t(item.displayNameKey as string)}`"
          role="button"
          hover-opacity="0.10"
        >
          <VaSidebarItemContent class="py-1 pr-2" :style="`padding-left: ${level + 1}rem`">
            <VaIcon v-if="item.icon" aria-hidden="true" :name="item.icon" size="20px" :color="iconColor(item)" />
            <VaSidebarItemTitle class="flex justify-between items-center leading-4 font-semibold">
              {{ item.displayName ?? t(item.displayNameKey as string) }}
              <VaIcon v-if="item.children" :name="arrowDirection(isCollapsed)" size="20px" />
            </VaSidebarItemTitle>
          </VaSidebarItemContent>
        </VaSidebarItem>
      </template>
      <template #body>
        <div v-for="child in item.children" :key="child.id">
          <!-- Single menu item without children -->
          <SidebarMenuItem
            v-if="child.children"
            :key="child.id"
            :item="child"
            :active-color="props.activeColor"
            :icon-color="props.iconColor"
            :text-color="props.textColor"
            :arrow-direction="props.arrowDirection"
            :handle-favorite="props.handleFavorite"
            :level="props.level + 1"
            :force-expand="props.forceExpand"
          />

          <SidebarLinkItem
            v-else
            :item="child"
            :active="isActiveChildRoute(child)"
            :active-color="activeColor"
            :text-color="textColor(child)"
            :icon-color="iconColor(child)"
            :favorite-color="props.iconColor(child)"
            :level="level + 2"
            content-class="py-1 pr-2"
            title-class="leading-4 font-semibold text-wrap"
            external-icon-size="small"
            :handle-favorite="props.handleFavorite"
          />
        </div>
      </template>
    </VaCollapse>
  </VaAccordion>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue"
import SidebarMenuItem from "./SidebarMenuItem.vue"
import SidebarLinkItem from "./SidebarLinkItem.vue"
import { MenuHierarchy } from "@/pages/admin/menuHierarchy/types"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"

const { t } = useI18n()
const value = ref<boolean[]>([])
const route = useRoute()

const props = defineProps<{
  item: MenuHierarchy
  activeColor: string
  iconColor: (item: MenuHierarchy) => string
  textColor: (item: MenuHierarchy) => string
  arrowDirection: (isCollapsed: boolean) => string
  handleFavorite: (item: MenuHierarchy) => void
  level: number
  forceExpand?: boolean
}>()

const isActiveChildRoute = (child: MenuHierarchy) => {
  if (route.name == "help-display") {
    const paramJson = child.routeParam ? JSON.parse(child.routeParam) : {}
    return route.params.uniqueId == paramJson.uniqueId
  }
  if (route.name == "report-viewer") {
    const paramJson = child.routeParam ? JSON.parse(child.routeParam) : {}
    return route.params.id == paramJson.id
  }

  return route.name === child.route
}

const routeHasActiveChild = (section: MenuHierarchy) => {
  if (!section.children && route.name) {
    return isActiveRoute(section)
  } else if (section.children && route.name) {
    return section.children.some((item) => isActiveRoute(item))
  }

  return false
}

const isActiveRoute = (child: MenuHierarchy): boolean => {
  if (!child.route) return false
  if (child.route === "help-display") {
    const paramJson = child.routeParam ? JSON.parse(child.routeParam) : {}
    return route.params.uniqueId == paramJson.uniqueId
  }
  if (child.route === "report-viewer") {
    const paramJson = child.routeParam ? JSON.parse(child.routeParam) : {}
    return route.params.id == paramJson.id
  }
  return route!.name!.toString().endsWith(`${child.route}`)
}

const checkActive = () => {
  if (props.forceExpand) {
    value.value = [true]
    return
  }
  value.value = [routeHasActiveChild(props.item)]
}

watch(
  () => props.item,
  () => checkActive(),
  { immediate: true },
)

watch(
  () => props.forceExpand,
  () => checkActive(),
  { immediate: true },
)
</script>
